import type { ApolloError } from '@apollo/client';
import { endOfDay, startOfDay } from 'date-fns';
import { useTypedQuery } from '../lib/generated/zeus/apollo';
import { genericScalars } from '../lib/graphql/helpers';
import { userFatigueViolationReportSelector } from './selectors/fatigue-report';
import type { UserFatigueViolationReport } from './types';

export const queryUserFatigueReportName = 'queryUserFatigueReport';

/**
 * Queries the fatigue report for the provided users for the provided date range.
 * @param userIds ids of the users that will be used to retrieve the report.
 * @param from date of the shifts query to calculate the fatigue.
 * @param to date of the shifts query to calculate the fatigue.
 * @param onCompleted is an optional callback for handling result.
 * @returns fatigue reports for the provided users.
 */
const useQueryUserFatigueReport = (
  userIds: readonly string[],
  from: Date,
  to: Date,
  onError: (e: ApolloError) => void,
  onCompleted?: (data: readonly UserFatigueViolationReport[]) => void
) =>
  useTypedQuery(
    {
      queryUserFatigueReport: [
        {
          fromDate: startOfDay(from),
          toDate: endOfDay(to),
          userIds: [...userIds],
        },
        userFatigueViolationReportSelector,
      ],
    },
    {
      apolloOptions: {
        onCompleted: (data) => {
          if (
            data.queryUserFatigueReport === undefined ||
            onCompleted === undefined
          ) {
            return data;
          }

          const queryUserFatigueReport: readonly UserFatigueViolationReport[] =
            data.queryUserFatigueReport.filter(
              (r): r is UserFatigueViolationReport => typeof r !== 'string'
            );

          onCompleted(queryUserFatigueReport);
        },
        onError,
        skip: userIds.length === 0,
      },
      scalars: genericScalars,
      operationOptions: {
        operationName: queryUserFatigueReportName,
      },
    }
  );

export default useQueryUserFatigueReport;
